import React from 'react';
import { useNavigate } from 'react-router-dom';
import unwrapLoveLandingPage from '../assets/unwrap-love-landing-page.png';
import unwrapLoveCanvas from '../assets/unwrap-love-canvas.gif';
import unwrapLoveMoodboard from '../assets/unwrap-love-moodboard.gif';
import { projectDetailThemeStyles } from '../projectThemes';
import './ProjectDetailLayout.css';
import './UnwrapLoveProject.css';

export const UnwrapLoveProject: React.FC = () => {
  const navigate = useNavigate();

  return (
    <main
      className="project-detail-page unwrap-love-project-page"
      style={projectDetailThemeStyles.unwrapLove}
    >
      <aside className="project-page__sidebar">
        <button type="button" className="project-page__back" onClick={() => navigate(-1)}>
          <span className="project-page__back-arrow" aria-hidden="true">
            &lt;-
          </span>
          back to showcase
        </button>
        <div className="project-page__sidebar-summary">
          <p className="project-page__eyebrow">experience concept / visual story</p>
          <h1 className="project-page__title" aria-label="Unwrap Love">
            Unwrap Love
          </h1>
          <p className="project-page__description">
            A visual concept for turning small acts of care into moments people can discover,
            share, and remember.
          </p>
          <div className="project-page__tech-list" aria-label="Project disciplines">
            <span>Product concept</span>
            <span>Experience design</span>
            <span>Visual storytelling</span>
          </div>
        </div>
      </aside>

      <section className="project-page__content" aria-label="Project content">
        <div className="project-page__section">
          <h2 className="project-page__section-title">
            Turn Feelings into a Gift Someone Can Unwrap
          </h2>
          <div className="project-page__section-body">
            <p>
              <strong>Unwrap Love</strong> is an interactive digital gifting platform designed to
              help people express emotions in a more personal way.
            </p>
            <figure className="project-page__inline-figure unwrap-love-project__product-figure">
              <img
                className="project-page__inline-image"
                src={unwrapLoveLandingPage}
                alt="Unwrap Love landing page with the message Where Every Click is a Hug"
              />
            </figure>
            <p>
              Rather than sending a traditional e-card, users can freely compose photos,
              messages, voice recordings, music, and sketches on an open canvas, transforming
              memories into a digital gift that feels unique, emotional, and worth preserving.
            </p>
            <p>
              The experience is built around three moments: <strong>Create</strong> a shared
              memory. <strong>Send</strong> it to someone special. <strong>Watch</strong> them
              unwrap it as an experience, not just a message.
            </p>
          </div>
        </div>
        <div className="project-page__section unwrap-love-project__canvas-section">
          <h2 className="project-page__section-title">A Canvas for Personal Stories</h2>
          <div className="project-page__section-body">
            <p>
              <strong>Start with a relationship, not a template.</strong>
            </p>
            <p>
              Every gift begins with a blank canvas instead of a predefined layout.
            </p>
            <p>
              Users can freely arrange photos, notes, voice recordings, music, stickers,
              sketches, and AI-generated images into one composition. Every element can be
              moved, layered, resized, and rotated, making the experience feel more like
              creating a scrapbook than filling out a template.
            </p>
            <p>Rather than fitting memories into a design, the design grows around the story.</p>
            <figure className="project-page__inline-figure unwrap-love-project__story-figure">
              <img
                className="project-page__inline-image"
                src={unwrapLoveMoodboard}
                alt="Animated Unwrap Love moodboard showing two collaborative blank canvases becoming personal gifts"
              />
            </figure>
          </div>
        </div>
        <div className="project-page__section">
          <h2 className="project-page__section-title">Designed Around Expression</h2>
          <div className="project-page__section-body">
            <p>
              <strong>More than words.</strong>
            </p>
            <p>Different memories call for different ways of expression.</p>
            <p>
              Whether it&apos;s a voice recording, a favorite song, a handwritten note, or a
              collection of photos, every medium lives naturally on the same canvas, allowing
              emotion to be expressed without switching between separate tools.
            </p>
            <figure className="project-page__inline-figure unwrap-love-project__story-figure">
              <img
                className="project-page__inline-image"
                src={unwrapLoveCanvas}
                alt="Animated Unwrap Love canvas combining music, voice, photos, notes, stickers, and sketches"
              />
            </figure>
          </div>
        </div>
        <div className="project-page__section">
          <h2 className="project-page__section-title">From Creating to Unwrapping</h2>
          <div className="project-page__section-body">
            <p>
              <strong>The gift doesn&apos;t end when it&apos;s finished.</strong>
            </p>
            <p>
              After creating, users can preview the experience, schedule delivery, or send a
              personalized link. Friends can also contribute to the same canvas in real time,
              turning individual memories into something shared.
            </p>
            <p>
              When the recipient opens the gift, the original composition is preserved exactly
              as intended, making the experience feel like unwrapping something carefully
              prepared.
            </p>
          </div>
        </div>
        <div className="project-page__section">
          <h2 className="project-page__section-title">Designed to Be Kept</h2>
          <div className="project-page__section-body">
            <p>Digital gifts are often viewed once and forgotten.</p>
            <p>
              Unwrap Love explores a different approach, combining freeform creation,
              multimedia storytelling, and a thoughtful unwrapping experience to create
              something people want to revisit and keep.
            </p>
          </div>
        </div>
      </section>
    </main>
  );
};
